/**
 * Bulk Notes Panel Component
 * 
 * Inline expandable panel for bulk updating notes on transactions
 * Similar to the BulkCategorisePanel
 */

import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { useBulkUpdateTransactions } from '../../hooks/useTransactions';
import { Transaction } from '../../types/transaction.types';

interface BulkNotesPanelProps {
  isExpanded: boolean;
  onToggle: () => void;
  transactionIds: string[];
  transactions: Transaction[];
  onComplete: (shouldClose?: boolean) => void;
  onClose: () => void;
}

export function BulkNotesPanel({
  isExpanded,
  onToggle: _onToggle,
  transactionIds,
  transactions: _transactions,
  onComplete,
  onClose
}: BulkNotesPanelProps): React.ReactElement {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ updated: number; failedCount: number; failed?: Array<{ transactionId: string; error: string }> } | null>(null);

  const bulkUpdateMutation = useBulkUpdateTransactions();

  const handleApply = async () => {
    setError(null);
    setSuccess(null);

    try {
      const result = await bulkUpdateMutation.mutateAsync({
        transactionIds,
        updates: { notes: notes.trim() || undefined }
      });

      setSuccess({
        updated: result.updated,
        failedCount: result.failedCount,
        failed: result.failed
      });

      // Only auto-close on complete success (no failures)
      if (result.failedCount === 0) {
        onComplete(true);
        // Auto-close after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        // Still refresh the list, but don't close the panel
        onComplete(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notes');
    }
  };

  const handleClose = () => {
    setNotes('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isExpanded) {
    return <></>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm mt-2">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Bulk Update Notes
          </h3>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            aria-label="Close bulk notes panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter notes for all selected transactions..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This will update notes for {transactionIds.length} transaction{transactionIds.length !== 1 ? 's' : ''}. Leave empty to clear notes.
            </p>
          </div>

          {error && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {success.updated} transaction{success.updated !== 1 ? 's' : ''} updated successfully
                </p>
                {success.failedCount > 0 && (
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    {success.failedCount} transaction{success.failedCount !== 1 ? 's' : ''} failed to update
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={bulkUpdateMutation.isPending}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center space-x-2"
            >
              {bulkUpdateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Apply to {transactionIds.length} transaction{transactionIds.length !== 1 ? 's' : ''}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

